/**
 * Canvas sidebar panel for displaying rendered canvas content.
 * Resizable, with status indicator and canvas selector.
 *
 * Tasks: T034 (sidebar with resize), T035 (status/progress), T036 (canvas selector),
 *        T049 (limit warning), T066 (collapse/expand animation)
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Layers, X, ChevronDown, Loader2, CheckCircle, AlertCircle, AlertTriangle, Component } from 'lucide-react';
import type { Canvas, CanvasComponent, CanvasStatus } from '../../types/canvas';
import { MAX_CANVASES } from '../../types/canvas';
import { CanvasRenderer } from './CanvasRenderer';
import { useCanvasActions } from '../../hooks/useCanvasActions';

// ============================================================================
// Status Indicator (T035)
// ============================================================================

interface StatusIndicatorProps {
  status: CanvasStatus;
  progress?: number;
}

function StatusIndicator({ status, progress }: StatusIndicatorProps) {
  const statusConfig = {
    idle: {
      icon: <div className="w-2 h-2 rounded-full bg-gray-400" />,
      label: 'Idle',
      color: 'text-gray-400',
    },
    streaming: {
      icon: <Loader2 size={14} className="animate-spin text-sky-500" />,
      label: progress !== undefined ? `${Math.round(progress * 100)}%` : 'Loading...',
      color: 'text-sky-500',
    },
    complete: {
      icon: <CheckCircle size={14} className="text-green-500" />,
      label: 'Complete',
      color: 'text-green-500',
    },
    error: {
      icon: <AlertCircle size={14} className="text-red-500" />,
      label: 'Error',
      color: 'text-red-500',
    },
  };

  const config = statusConfig[status];

  return (
    <div className={`flex items-center gap-1.5 text-xs ${config.color}`}>
      {config.icon}
      <span>{config.label}</span>
    </div>
  );
}

// ============================================================================
// Progress Bar (T035)
// ============================================================================

interface ProgressBarProps {
  progress: number;
  show: boolean;
}

function ProgressBar({ progress, show }: ProgressBarProps) {
  if (!show) return null;

  return (
    <div className="h-1 bg-gray-200 dark:bg-gray-700 overflow-hidden">
      <div
        className="h-full bg-sky-500 transition-all duration-300"
        style={{ width: `${Math.round(progress * 100)}%` }}
      />
    </div>
  );
}

// ============================================================================
// Canvas Selector Dropdown (T036)
// ============================================================================

interface CanvasSelectorProps {
  canvases: Canvas[];
  activeCanvasId: string | null;
  onSelect: (canvasId: string) => void;
  onClose?: (canvasId: string) => void;
}

function CanvasSelector({ canvases, activeCanvasId, onSelect, onClose }: CanvasSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeCanvas = canvases.find((c) => c.id === activeCanvasId);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (canvases.length <= 1) {
    return (
      <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
        {activeCanvas?.id || 'No canvas'}
      </span>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
      >
        <span className="truncate max-w-[120px]">{activeCanvas?.id || 'Select canvas'}</span>
        <ChevronDown
          size={14}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 overflow-hidden">
          {canvases.map((canvas) => (
            <div
              key={canvas.id}
              className={`flex items-center justify-between transition-colors ${
                canvas.id === activeCanvasId
                  ? 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <button
                onClick={() => {
                  onSelect(canvas.id);
                  setIsOpen(false);
                }}
                className="flex-1 flex items-center justify-between px-3 py-2 text-sm text-left min-w-0"
              >
                <span className="truncate">{canvas.id}</span>
                <StatusIndicator status={canvas.status} />
              </button>
              {onClose && canvases.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose(canvas.id);
                  }}
                  className="p-1.5 mr-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Close canvas"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Component Renderer (with actions)
// ============================================================================

interface ComponentRendererProps {
  component: CanvasComponent;
  canvasId: string;
}

function ComponentRenderer({ component, canvasId }: ComponentRendererProps) {
  const { sendAction, actionState, clearError } = useCanvasActions({
    canvasId,
    artifactId: component.artifactId,
  });

  // Track last action for retry
  const lastActionRef = React.useRef<{ name: string; params?: Record<string, unknown> } | null>(null);

  const handleAction = useCallback(
    async (action: { name: string; params?: Record<string, unknown> }) => {
      lastActionRef.current = action;
      await sendAction(action);
    },
    [sendAction]
  );

  const handleRetry = useCallback(() => {
    if (lastActionRef.current) {
      clearError();
      sendAction(lastActionRef.current);
    }
  }, [sendAction, clearError]);

  return (
    <CanvasRenderer
      tree={component.jsonTree}
      onAction={handleAction}
      error={actionState.error || undefined}
      onRetry={actionState.error ? handleRetry : undefined}
    />
  );
}

// ============================================================================
// Active Canvas Renderer (renders all components in the canvas)
// ============================================================================

interface ActiveCanvasRendererProps {
  canvas: Canvas;
}

function ActiveCanvasRenderer({ canvas }: ActiveCanvasRendererProps) {
  // Get all components sorted by creation time (oldest first)
  const components = useMemo(() => {
    return Array.from(canvas.components.values()).sort(
      (a, b) => a.createdAt - b.createdAt
    );
  }, [canvas.components]);

  if (components.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500">
        <Component className="w-12 h-12 mb-3 opacity-50" />
        <p className="text-sm">No components in canvas</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {components.map((component) => (
        <div key={component.id} className="relative">
          {component.status === 'error' && (
            <div className="absolute -top-2 -right-2 z-10">
              <AlertCircle size={16} className="text-red-500" />
            </div>
          )}
          {component.status === 'streaming' && (
            <div className="absolute -top-2 -right-2 z-10">
              <Loader2 size={16} className="text-sky-500 animate-spin" />
            </div>
          )}
          <ComponentRenderer component={component} canvasId={canvas.id} />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Limit Warning Banner (T049)
// ============================================================================

interface LimitWarningProps {
  canvasCount: number;
}

function LimitWarning({ canvasCount }: LimitWarningProps) {
  if (canvasCount < MAX_CANVASES) return null;

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 text-xs">
      <AlertTriangle size={14} className="flex-shrink-0" />
      <span>
        Canvas limit ({MAX_CANVASES}) reached. Close a canvas to add more.
      </span>
    </div>
  );
}

// ============================================================================
// Canvas Sidebar (T034)
// ============================================================================

export interface CanvasSidebarProps {
  isOpen: boolean;
  canvases: Canvas[];
  activeCanvasId: string | null;
  onClose: () => void;
  onSelectCanvas: (canvasId: string) => void;
  onCloseCanvas?: (canvasId: string) => void;
  width?: number;
  onWidthChange?: (width: number) => void;
}

export function CanvasSidebar({
  isOpen,
  canvases,
  activeCanvasId,
  onClose,
  onSelectCanvas,
  onCloseCanvas,
  width: controlledWidth,
  onWidthChange,
}: CanvasSidebarProps) {
  // Use controlled width if provided, otherwise manage internally
  const [internalWidth, setInternalWidth] = useState(controlledWidth ?? 400);
  const width = controlledWidth ?? internalWidth;

  const setWidth = useCallback((newWidth: number) => {
    setInternalWidth(newWidth);
    onWidthChange?.(newWidth);
  }, [onWidthChange]);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const activeCanvas = canvases.find((c) => c.id === activeCanvasId);

  // Auto-scroll: track whether user is at the bottom of the content area
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior,
      });
    }
  }, []);

  const handleContentScroll = useCallback(() => {
    if (!contentRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    setIsAtBottom(scrollHeight - scrollTop - clientHeight < 50);
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      el.addEventListener('scroll', handleContentScroll, { passive: true });
      return () => el.removeEventListener('scroll', handleContentScroll);
    }
  }, [handleContentScroll]);

  // Reset scroll tracking when switching canvases
  useEffect(() => {
    setIsAtBottom(true);
  }, [activeCanvasId]);

  // Auto-scroll when canvas content updates and user is near the bottom
  useEffect(() => {
    if (activeCanvas && isAtBottom) {
      scrollToBottom('auto');
    }
  }, [activeCanvas?.updatedAt, scrollToBottom, isAtBottom]);

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      setWidth(Math.max(300, Math.min(800, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Return null when closed (matching PlanSidebar pattern for stability)
  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={sidebarRef}
      className="flex flex-col h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 relative"
      style={{ width: `${width}px` }}
      data-testid="canvas-sidebar"
    >
      {/* Resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-sky-400 transition-colors z-10"
        onMouseDown={handleMouseDown}
        style={{ backgroundColor: isResizing ? '#38bdf8' : 'transparent' }}
      />

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Layers size={18} className="text-indigo-600 flex-shrink-0" />
          <CanvasSelector
            canvases={canvases}
            activeCanvasId={activeCanvasId}
            onSelect={onSelectCanvas}
            onClose={onCloseCanvas}
          />
          {canvases.length > 1 && (
            <span className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
              {canvases.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeCanvas && <StatusIndicator status={activeCanvas.status} />}
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
            title="Close canvas panel"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Progress bar for streaming */}
      {activeCanvas?.status === 'streaming' &&
        activeCanvas.progress !== undefined &&
        activeCanvas.progress >= 0 &&
        activeCanvas.progress <= 1 && (
          <ProgressBar progress={activeCanvas.progress} show={true} />
        )}

      {/* T049: Limit warning banner */}
      <LimitWarning canvasCount={canvases.length} />

      {/* Content */}
      <div ref={contentRef} className="flex-1 overflow-y-auto p-4 bg-white dark:bg-gray-900">
        {!activeCanvas ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-600">
            <Layers className="w-10 h-10 mb-3 opacity-50" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No canvas selected</p>
          </div>
        ) : (
          <>
            {/* Show canvas-level error as banner, but still render components */}
            {activeCanvas.status === 'error' && activeCanvas.error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-400">
                <div className="flex items-center gap-2 text-sm">
                  <AlertCircle size={14} className="flex-shrink-0" />
                  <span>{activeCanvas.error}</span>
                </div>
              </div>
            )}
            {/* Always render components - individual error boundaries handle component errors */}
            <ActiveCanvasRenderer canvas={activeCanvas} />
          </>
        )}
      </div>

      {/* Footer with canvas info */}
      {activeCanvas && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
            <span>
              {activeCanvas.components.size} component{activeCanvas.components.size !== 1 ? 's' : ''}
            </span>
            <span>
              {new Date(activeCanvas.updatedAt).toLocaleTimeString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default CanvasSidebar;
